import 'react-image-crop/dist/ReactCrop.css';

import React, { useEffect, useMemo, useRef, useState } from 'react';

import cx from 'classnames';
import PropTypes from 'prop-types';
import CSSModule from 'react-css-modules';
import { useDropzone } from 'react-dropzone';
import ReactCrop from 'react-image-crop';
import { FormattedMessage, injectIntl } from 'react-intl';

import { Button, Feedback, Modal, Spinner } from 'components';
import { getCroppedImg } from 'utils/images';

import styles from './FormImageField.module.scss';
import { DeleteButtonIcon,RedoButtonIcon } from './icons';
import messages from './messages';

const MAX_FILE_SIZE_MB = 2; // Restricting file size to 2MB
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024; // Convert MB to bytes

// FIX (react-image-crop v11 migration): `aspect` used to live inside the
// crop state object in the old API. In v11 it's a separate top-level prop
// on <ReactCrop aspect={...}> instead, so it's been pulled out of this
// default and applied directly on the component below. The crop object
// itself now needs x/y/height too (previously only width was set, which
// the old API tolerated but v11's Crop shape expects fully).
const cropDefault = {
  unit: '%',
  x: 25,
  y: 25,
  width: 50, // Default square crop selection
  height: 50,
};
const CROP_ASPECT = 1; // square aspect ratio (1:1) — was cropDefault.aspect before v11

const FormImage = ({
  intl,
  textDark,
  row,
  rounded,
  width,
  height,
  aspect,
  accept,
  label,
  input,
  readOnly,
  meta,
  instantUpload,
  loading,
  error,
  onChange,
  onDelete,
  url,
}) => {
  //Todo seperate this style from js to scss
  const img_upload_status_container_style = useMemo(
    () => ({
      position: 'absolute',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%,-50%)',
    }),
    [],
  );

  const [crop, setCrop] = useState(cropDefault);
  const [cropperImgRef, setCropperImgRef] = useState();
  const [isCropperOpened, setCropperOpened] = useState(false);
  const [originalFile, setOriginalFile] = useState();
  const [originalFileUrl, setOriginalFileUrl] = useState();
  const [previewUrl, setPreviewUrl] = useState();
  const [fileSizeError, setFileSizeError] = useState(''); // error state for file size validation

  useEffect(() => {
    if (instantUpload) {
      input.onChange(url);
      setPreviewUrl(url);
    }
  }, [url]);

  useEffect(() => {
    if (!input.value) return;
    if (input.value instanceof Blob) {
      setPreviewUrl(URL.createObjectURL(input.value));
    } else if (typeof input.value === 'string') {
      /* Support for @string URL form value */
      setPreviewUrl(input.value);
    }
  }, [input.value]);

  const onDrop = acceptedFiles => {
    if (acceptedFiles.length > 0) {
      const file = acceptedFiles[0];

      // check file size before proceeding
      if (file.size > MAX_FILE_SIZE_BYTES) {
        setFileSizeError(
          `We can only accept images below ${MAX_FILE_SIZE_MB} MB of data`,
        );
        return;
      }
      setFileSizeError('');

      const fileUrl = URL.createObjectURL(file);
      setOriginalFile(file);
      setOriginalFileUrl(fileUrl);
      setCropperOpened(true);
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    disabled: readOnly,
    multiple: false,
    accept,
  });

  const clearOriginalFile = () => {
    setOriginalFile();
    setOriginalFileUrl();
  };

  const closeCropper = () => {
    setCropperOpened(false);
    setCrop(cropDefault);
  };

  // FIX: v11's onChange signature is (pixelCrop, percentCrop) — the first
  // arg is now always pixel-based regardless of the crop's configured unit,
  // which is a behavior change from the old API. This form's crop state has
  // always been percent-based (`unit: '%'` in cropDefault), and getCroppedImg
  // below receives whatever's stored in `crop`, so percentCrop is used here
  // to preserve that original percent semantics.
  // NEEDS VERIFICATION: confirm utils/images.js's getCroppedImg expects a
  // percent-unit crop shape — if it does its own pixel math instead, this
  // may need to switch to the first (pixelCrop) argument instead.
  const onCropChange = (_pixelCrop, percentCrop) => {
    setCrop(percentCrop);
  };

  const onCropDone = async () => {
    if (!cropperImgRef) return;
    const blob = await getCroppedImg(
      cropperImgRef,
      crop,
      originalFile.name,
      true,
    );
    if (!instantUpload) {
      input.onChange(blob);
    } else {
      setPreviewUrl(URL.createObjectURL(blob));
      onChange && onChange(blob);
    }
    closeCropper();
  };

  const onCropperClose = () => {
    clearOriginalFile();
    closeCropper();
    setCropperImgRef();
  };

  const onImageDelete = () => {
    input.onChange('');
    clearOriginalFile();
    onDelete();
  };

  const placeholder = useRef();

  const imgPlaceholder = (
    <div
      ref={placeholder}
      style={{
        maxWidth: width,
        maxHeight: height,
        // Dynamic height set, to save valid aspect.
        // Set height on initial render.
        height: placeholder.current
          ? placeholder.current.offsetWidth / aspect
          : height,
      }}
      styleName={cx('img-placeholder', {
        rounded,
        'drag-active': isDragActive,
      })}
    >
      <span>{width + 'x' + height}</span>
    </div>
  );

  const imgPreview = instantUpload ? (
    <div style={{ position: 'relative' }}>
      <img
        styleName={cx({ rounded, 'drag-active': isDragActive })}
        className="img-fluid"
        style={{ width }}
        src={previewUrl}
        alt={intl.formatMessage(messages.previewImgAlt)}
      />
      {(loading || !!error) && (
        <div style={img_upload_status_container_style}>
          {loading && <Spinner show />}
          {!loading && !!error && (
            <img
              src={RedoButtonIcon}
              width={30}
              style={{ opacity: '0.8' }}
              alt="retry"
            />
          )}
        </div>
      )}
    </div>
  ) : (
    <img
      styleName={cx({ rounded, 'drag-active': isDragActive })}
      className="img-fluid"
      style={{ width }}
      src={previewUrl}
      alt={intl.formatMessage(messages.previewImgAlt)}
    />
  );
  const shouldDisplayCropperDoneButton = !!cropperImgRef;
  const shouldDisplayError = !!(meta.error && (!meta.pristine || meta.touched));

  return (
    <div
      className="mt-3"
      styleName={cx('img-container', { 'text-dark': textDark })}
    >
      <div
        {...getRootProps()}
        className={cx('d-flex align-items-center', { 'flex-column': !row })}
      >
        <input {...getInputProps()} />
        {fileSizeError && (
          <div className="text-danger mb-2">{fileSizeError}</div>
        )}{' '}
        {/* display file size error */}
        {previewUrl ? (
          <img
            styleName={cx({ rounded, 'drag-active': isDragActive })}
            className="img-fluid"
            style={{ width, height: width }} // ensuring square display
            src={previewUrl}
            alt={intl.formatMessage(messages.previewImgAlt)}
          />
        ) : (
          <div
            style={{ width, height: width }} // placeholder for square format
            styleName={cx('img-placeholder', {
              rounded,
              'drag-active': isDragActive,
            })}
          >
            <span>{width + 'x' + width}</span>
          </div>
        )}
        <Feedback
          textCenter
          fullWidth={false}
          show={shouldDisplayError}
          content={meta.error}
          invalid
        />
      </div>
      {instantUpload && url && (
        <div style={{ textAlign: 'center', width: '100%' }}>
          <img
            width={'15px'}
            alt="delete button"
            src={DeleteButtonIcon}
            onClick={onImageDelete}
          />
        </div>
      )}

      <Modal
        title={intl.formatMessage(messages.croppingModalTitle)}
        isOpen={isCropperOpened}
        onRequestClose={closeCropper}
      >
        <div className="d-flex flex-column align-items-center">
          <ReactCrop
            crop={crop}
            aspect={CROP_ASPECT}
            ruleOfThirds
            keepSelection
            onChange={onCropChange}
          >
            <img
              src={originalFileUrl}
              alt=""
              onLoad={e => setCropperImgRef(e.currentTarget)}
            />
          </ReactCrop>
          <Button onClick={onCropDone} className="mt-3">
            <FormattedMessage {...messages.croppingModalDoneButtonText} />
          </Button>
        </div>
      </Modal>
    </div>
  );
};

FormImage.propTypes = {
  intl: PropTypes.object.isRequired,
  rounded: PropTypes.bool,
  width: PropTypes.number,
  height: PropTypes.number,
  aspect: PropTypes.number,
  accept: PropTypes.string,
  label: PropTypes.string,
  input: PropTypes.object,
  meta: PropTypes.object,
  textDark: PropTypes.bool,
  readOnly: PropTypes.bool,
  row: PropTypes.bool,
  instantUpload: PropTypes.bool,
  loading: PropTypes.bool,
  error: PropTypes.string,
  onChange: PropTypes.func,
  onDelete: PropTypes.func,
  url: PropTypes.string,
};

FormImage.defaultProps = {
  instantUpload: false,
  loading: false,
  error: '',
  url: '',
};

export default injectIntl(
  CSSModule(FormImage, styles, { allowMultiple: true }),
);